import {
  GetAccountsForTransferFromResponse,
  GetAccountsForTransferToResponse,
  TransferAccountDto,
  TransferAccountOption,
  TransferGroupedAccounts,
  TransferPreview,
  TransferPreviewResponse,
  TransferServiceKey,
  TransferServiceOption,
} from "../model/types";

const SERVICE_LABELS: Record<TransferServiceKey, string> = {
  google: "Google",
  yandex: "Yandex",
  fb: "Meta",
  tiktok: "TikTok",
  mailru: "MyTarget",
  vk: "ВКонтакте",
  vkads: "VK Ads",
};

export const mapServiceKeyToOption = (
  serviceKey: TransferServiceKey,
): TransferServiceOption => ({
  value: serviceKey,
  label: SERVICE_LABELS[serviceKey] ?? serviceKey,
});

export const mapTransferAccountDtoToOption = (
  dto: TransferAccountDto,
  serviceKey: TransferServiceKey,
): TransferAccountOption => {
  const accountName =
    serviceKey === "yandex" ? dto.account_login : dto.account_login_name;

  return {
    value: dto.account_id,
    label: `${accountName} (${dto.account_id})`,
    psaaId: dto.id,
    accountId: dto.account_id,
    accountLogin: dto.account_login,
    accountSite: dto.account_site,
    serviceKey,
    serviceName: dto.service_name,
    currency: dto.contract_service_currency,
    provider: dto.provider,
    isDisabled: !dto.provider.transfer_enabled,
    disabledReason: dto.provider.transfer_enabled
      ? undefined
      : dto.provider.transfer_tooltip,
  };
};

const mapGroupedDto = (
  data: Partial<Record<TransferServiceKey, TransferAccountDto[]>>,
): TransferGroupedAccounts => {
  const result: TransferGroupedAccounts = {};

  (Object.keys(data) as TransferServiceKey[]).forEach((serviceKey) => {
    const accounts = data[serviceKey] ?? [];
    result[serviceKey] = accounts.map((item) =>
      mapTransferAccountDtoToOption(item, serviceKey),
    );
  });

  return result;
};

export const mapFromAccountsResponse = (
  response: GetAccountsForTransferFromResponse,
): TransferGroupedAccounts => mapGroupedDto(response.data);

export const mapToAccountsResponse = (
  response: GetAccountsForTransferToResponse,
): TransferGroupedAccounts => mapGroupedDto(response.data);

export const mapGroupedAccountsToServiceOptions = (
  groupedAccounts: TransferGroupedAccounts,
): TransferServiceOption[] => {
  return (Object.keys(groupedAccounts) as TransferServiceKey[])
    .filter((key) => (groupedAccounts[key]?.length ?? 0) > 0)
    .map(mapServiceKeyToOption);
};

export const mapPreviewResponse = (
  response: TransferPreviewResponse,
): TransferPreview => ({
  accountFrom: {
    id: response.data.account_from.id,
    currentBalance: response.data.account_from.current_balance,
    newBalance: response.data.account_from.new_balance,
    toWithdraw: response.data.account_from.to_withdraw,
    burden: response.data.account_from.burden
      ? {
          type: response.data.account_from.burden.type,
          amount: response.data.account_from.burden.amount,
          currency: response.data.account_from.burden.currency,
          feeQuantity: response.data.account_from.burden.fee.quantity,
          feeUnit: response.data.account_from.burden.fee.unit,
        }
      : null,
  },
  accountsTo: response.data.accounts_to.map((item) => ({
    id: item.id,
    amount: item.amount,
    currency: item.currency,
    currentBalance: item.current_balance,
    newBalance: item.new_balance,
    fee: item.fee
      ? {
          type: item.fee.type,
          quantity: item.fee.quantity,
          unit: item.fee.unit,
        }
      : null,
  })),
  validationResult: {
    isValid: response.validation_result.is_valid,
    errors: response.validation_result.errors.map((item) => ({
      accountId: item.account_id,
      error: item.error,
    })),
  },
});
